import { Box, Flex, HStack, Link, SimpleGrid, Text } from "@chakra-ui/react";
import { Mail, Phone, Wrench } from "lucide-react";
import { usePublicSettings } from "@modules/shared";
import { AMBER, NAVY } from "./accent";
import { LAYOUT_MAX, LAYOUT_PX } from "./layoutConstants";

const QUICK_LINKS = [
  { label: "Shop", url: "/shop" },
  { label: "Cart", url: "/cart" },
  { label: "Brands", url: "/brands" },
  { label: "Categories", url: "/categories" },
  { label: "Garage", url: "/vehicles" },
  { label: "Orders", url: "/orders" },
  { label: "Wishlist", url: "/wishlist" },
];

const LEGAL_LINKS = [
  { label: "Terms of Service", url: "/legal/terms" },
  { label: "Privacy Policy", url: "/legal/privacy" },
];

export type FooterProps = { onNavigate: (url: string) => void };

export const Footer = ({ onNavigate }: FooterProps) => {
  const settingsQuery = usePublicSettings();
  const store = settingsQuery.data?.store;

  return (
    <Box as="footer" bg={NAVY[900]} color="whiteAlpha.800" mt={12}>
      <Box maxW={LAYOUT_MAX.content} mx="auto" px={LAYOUT_PX} py={{ base: 8, md: 10 }}>
        <SimpleGrid columns={{ base: 1, md: 4 }} gap={8}>
          <Box>
            <HStack gap={2} mb={3}>
              <Flex
                align="center"
                justify="center"
                boxSize="30px"
                borderRadius="md"
                bgGradient={`linear(135deg, ${NAVY[700]}, ${AMBER[600]})`}
              >
                <Wrench size={16} color="#ffffff" />
              </Flex>
              <Text fontWeight="800" color="white">
                {store?.name ?? "Muthu Car Spares"}
              </Text>
            </HStack>
            <Text fontSize="sm" color="whiteAlpha.600">
              {store?.tagline ?? "Genuine spares for every vehicle, delivered to your door."}
            </Text>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={3} fontSize="sm" letterSpacing="0.04em">
              QUICK LINKS
            </Text>
            <Flex direction="column" gap={2}>
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.url}
                  fontSize="sm"
                  color="whiteAlpha.700"
                  _hover={{ color: AMBER[300] }}
                  onClick={() => onNavigate(link.url)}
                >
                  {link.label}
                </Link>
              ))}
            </Flex>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={3} fontSize="sm" letterSpacing="0.04em">
              LEGAL
            </Text>
            <Flex direction="column" gap={2}>
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.url}
                  fontSize="sm"
                  color="whiteAlpha.700"
                  _hover={{ color: AMBER[300] }}
                  onClick={() => onNavigate(link.url)}
                >
                  {link.label}
                </Link>
              ))}
            </Flex>
          </Box>

          <Box>
            <Text fontWeight="700" color="white" mb={3} fontSize="sm" letterSpacing="0.04em">
              SUPPORT
            </Text>
            <Flex direction="column" gap={2}>
              {store !== undefined && (
                <>
                  <HStack fontSize="sm" color="whiteAlpha.700" gap={2}>
                    <Mail size={14} color={AMBER[300]} />
                    <Link href={`mailto:${store.supportEmail}`} _hover={{ color: AMBER[300] }}>
                      {store.supportEmail}
                    </Link>
                  </HStack>
                  {store.supportPhone !== null && (
                    <HStack fontSize="sm" color="whiteAlpha.700" gap={2}>
                      <Phone size={14} color={AMBER[300]} />
                      <Link href={`tel:${store.supportPhone}`} _hover={{ color: AMBER[300] }}>
                        {store.supportPhone}
                      </Link>
                    </HStack>
                  )}
                </>
              )}
            </Flex>
          </Box>
        </SimpleGrid>

        <Box borderTopWidth="1px" borderColor="whiteAlpha.200" mt={8} pt={5}>
          <Text fontSize="xs" color="whiteAlpha.500" textAlign="center">
            © {new Date().getFullYear()} {store?.name ?? "Muthu Car Spares"}. All rights reserved.
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
